/**
 * Bank Detail — drill-in for one bank (PHASE2_SPEC §4.7).
 *
 * Route: `/banks/:orgId`
 * Layout:
 *   Breadcrumb · Header (name · ID · branch count · last submission)
 *   8 KPI tiles (loans · deposits · NPA · KYC etc.)
 *   Two donut charts (deposit mix · NPA mix)
 *   Top loan + deposit products (two side-by-side tables)
 *   Branch table (sortable; row → /banks/:orgId/branches/:branchId)
 *   DQ chip strip
 */

import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Card from '../../components/Card';
import Donut, { DonutLegend, type DonutDatum } from '../../components/Donut';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import StatusPill from '../../components/StatusPill';
import { formatDate, formatDateTime } from '../../helpers/dateFormat';
import { dqFlag, npaFlag } from '../../helpers/flag';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { DEPOSIT_COLORS, NPA_COLORS } from '../../helpers/palette';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchBankDetail, selectBankDetail } from '../../redux/slices/dashboard';
import type {
	BankDetailBranchRow,
	BankDetailTopProductDeposit,
	BankDetailTopProductLoan,
} from '../../type/api';

export default function BankDetailPage() {
	const params = useParams<{ orgId: string }>();
	const orgId = Number(params.orgId);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectBankDetail(orgId));

	useEffect(() => {
		if (!Number.isFinite(orgId)) return;
		if (cell.status === 'idle') {
			dispatch(fetchBankDetail({ orgId }));
		}
	}, [orgId, cell.status, dispatch]);

	const data = cell.data;
	const branches: BankDetailBranchRow[] = useMemo(
		() => (Array.isArray(data?.branches) ? data!.branches : []),
		[data],
	);
	const loanProducts: BankDetailTopProductLoan[] = useMemo(
		() => (Array.isArray(data?.topProducts?.loan) ? data!.topProducts.loan : []),
		[data],
	);
	const depositProducts: BankDetailTopProductDeposit[] = useMemo(
		() => (Array.isArray(data?.topProducts?.deposit) ? data!.topProducts.deposit : []),
		[data],
	);
	const branchSort = useSortable<BankDetailBranchRow>(branches, { field: 'loanCr', dir: 'desc' });

	const handleRetry = () => dispatch(fetchBankDetail({ orgId }));

	if (!Number.isFinite(orgId)) {
		return (
			<div className="page-body">
				<Card><EmptyState title="Invalid bank id" /></Card>
			</div>
		);
	}
	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Bank</h1></div></div>
				<Card><LoadingSkeleton rows={6} columns={6} /></Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Bank</h1></div></div>
				<ErrorBanner message={cell.error || 'Failed to load bank detail'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Bank</h1></div></div>
				<Card><EmptyState title="No data for this bank" /></Card>
			</div>
		);
	}

	const bank = data.bank;
	const k = data.kpis;
	const dq = data.dq;
	const sub = data.submission;

	const depositMixData: DonutDatum[] = [
		{ name: 'Savings', value: data.depositMix.savingsCr, color: DEPOSIT_COLORS.savings },
		{ name: 'FD', value: data.depositMix.fdCr, color: DEPOSIT_COLORS.fd },
		{ name: 'RD', value: data.depositMix.rdCr, color: DEPOSIT_COLORS.rd },
		{ name: 'Current', value: data.depositMix.currentCr, color: DEPOSIT_COLORS.current },
	];
	const npaMixData: DonutDatum[] = [
		{ name: 'Standard', value: data.npaMix.standardCr, color: NPA_COLORS.standard },
		{ name: 'Sub-Standard', value: data.npaMix.subStandardCr, color: NPA_COLORS.subStandard },
		{ name: 'Doubtful', value: data.npaMix.doubtfulCr, color: NPA_COLORS.doubtful },
		{ name: 'Loss', value: data.npaMix.lossCr, color: NPA_COLORS.loss },
	];

	return (
		<div className="page-body">
			<div className="breadcrumb">
				<Link to="/">Overview</Link>
				<span className="sep">/</span>
				<span>Banks</span>
				<span className="sep">/</span>
				<span>{bank.orgName}</span>
			</div>
			<div className="page-head">
				<div>
					<h1 className="page-title">{bank.orgName}</h1>
					<div className="page-sub">
						ID <b style={{ color: 'var(--text)' }}>{bank.orgId}</b> · {formatCount(bank.branchCount)} branches
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
					<div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>
						Last LOAN: {sub.loanLastAt ? formatDateTime(sub.loanLastAt) : '—'} ·{' '}
						Last DEPOSIT: {sub.depositLastAt ? formatDateTime(sub.depositLastAt) : '—'} ·{' '}
						Last KYC: {sub.kycLastAt ? formatDateTime(sub.kycLastAt) : '—'}
					</div>
				</div>
				<button type="button" className="btn ghost" onClick={() => navigate(-1)}>← Back</button>
			</div>

			<div className="grid cols-4">
				<KpiTile label="Loan ₹Cr" value={formatCrore(k.loanCr, 2)} sub={`${formatCount(k.loanCount)} accts`} />
				<KpiTile label="Deposit ₹Cr" value={formatCrore(k.depositCr, 2)} sub={`${formatCount(k.depositCount)} accts`} />
				<KpiTile label="NPA ₹Cr" value={formatCrore(k.npaCr, 2)} />
				<KpiTile
					label="NPA %"
					value={formatPct(k.npaPct)}
					flag={k.npaFlag || npaFlag(k.npaPct)}
					flagVariant="npa"
					primary
				/>
			</div>

			<div className="grid cols-4">
				<KpiTile label="KYC Accounts" value={formatCount(k.kycAccounts)} />
				<KpiTile label="Unique Customers" value={formatCount(k.uniqueCustomers)} />
				<KpiTile label="Branches" value={formatCount(bank.branchCount)} />
				<KpiTile label="Worst NPA Branch" value={branches.length ? formatPct(Math.max(...branches.map((b) => b.npaPct))) : '—'} />
			</div>

			<div className="grid cols-2">
				<Card title="Deposit mix">
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 18, alignItems: 'center' }}>
						<Donut data={depositMixData} />
						<DonutLegend data={depositMixData} />
					</div>
				</Card>
				<Card title="NPA mix">
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 18, alignItems: 'center' }}>
						<Donut data={npaMixData} />
						<DonutLegend data={npaMixData} />
					</div>
				</Card>
			</div>

			<div className="grid cols-2">
				<Card title={`Top loan products (${loanProducts.length})`}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<th>Code</th>
								<th>Product</th>
								<th className="num">Loan ₹Cr</th>
								<th className="num">Accounts</th>
								<th className="num">NPA %</th>
							</tr>
						</thead>
						<tbody>
							{loanProducts.map((p) => (
								<tr
									key={p.productCode}
									className="clickable"
									onClick={() => navigate(`/products/${encodeURIComponent(p.productCode)}?type=LOAN`)}
									tabIndex={0}
								>
									<td style={{ fontWeight: 600 }}>{p.productCode}</td>
									<td>{p.productName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(p.loanCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(p.loanCount)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={npaFlag(p.npaPct)} variant="npa" compact label={formatPct(p.npaPct)} />
									</td>
								</tr>
							))}
							{!loanProducts.length && (
								<tr><td colSpan={5}><EmptyState title="No loan products" /></td></tr>
							)}
						</tbody>
					</table>
				</Card>
				<Card title={`Top deposit products (${depositProducts.length})`}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<th>Code</th>
								<th>Product</th>
								<th className="num">Balance ₹Cr</th>
								<th className="num">Accounts</th>
							</tr>
						</thead>
						<tbody>
							{depositProducts.map((p) => (
								<tr
									key={p.productCode}
									className="clickable"
									onClick={() => navigate(`/products/${encodeURIComponent(p.productCode)}?type=DEPOSIT`)}
									tabIndex={0}
								>
									<td style={{ fontWeight: 600 }}>{p.productCode}</td>
									<td>{p.productName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(p.balanceCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(p.count)}</td>
								</tr>
							))}
							{!depositProducts.length && (
								<tr><td colSpan={4}><EmptyState title="No deposit products" /></td></tr>
							)}
						</tbody>
					</table>
				</Card>
			</div>

			<Card title={`Branches (${branches.length})`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="branchId" sort={branchSort}>Branch ID</SortableTh>
								<SortableTh field="branchName" sort={branchSort}>Name</SortableTh>
								<SortableTh field="loanCr" sort={branchSort} numeric>Loan ₹Cr</SortableTh>
								<SortableTh field="depositCr" sort={branchSort} numeric>Deposit ₹Cr</SortableTh>
								<SortableTh field="npaCr" sort={branchSort} numeric>NPA ₹Cr</SortableTh>
								<SortableTh field="npaPct" sort={branchSort} numeric>NPA %</SortableTh>
							</tr>
						</thead>
						<tbody>
							{branchSort.rows.map((b) => (
								<tr
									key={b.branchId}
									className="clickable"
									onClick={() => navigate(`/banks/${orgId}/branches/${encodeURIComponent(b.branchId)}`)}
									tabIndex={0}
								>
									<td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{b.branchId}</td>
									<td>{b.branchName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.loanCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.depositCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.npaCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										<StatusPill flag={npaFlag(b.npaPct)} variant="npa" compact label={formatPct(b.npaPct)} />
									</td>
								</tr>
							))}
							{!branchSort.rows.length && (
								<tr><td colSpan={6}><EmptyState title="No branches" /></td></tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>

			<Card title="Data quality">
				<div className="dq-strip">
					<span className={`chip ${dqFlag(dq.loanMissingPanPct).toLowerCase() === 'green' ? 'good' : dqFlag(dq.loanMissingPanPct).toLowerCase() === 'red' ? 'bad' : 'warn'}`}>
						Loan PAN missing: {formatPct(dq.loanMissingPanPct)}
					</span>
					<span className={`chip ${dqFlag(dq.loanMissingMobilePct).toLowerCase() === 'green' ? 'good' : dqFlag(dq.loanMissingMobilePct).toLowerCase() === 'red' ? 'bad' : 'warn'}`}>
						Loan Mobile missing: {formatPct(dq.loanMissingMobilePct)}
					</span>
					<span className={`chip ${dqFlag(dq.depositMissingNomineePct).toLowerCase() === 'green' ? 'good' : dqFlag(dq.depositMissingNomineePct).toLowerCase() === 'red' ? 'bad' : 'warn'}`}>
						Deposit Nominee missing: {formatPct(dq.depositMissingNomineePct)}
					</span>
				</div>
			</Card>
		</div>
	);
}
